import React, { useState, useEffect } from "react";
import { getAPIURL, getVendorID, getVendorUsers, getFormattedPrice } from "../../services/helper";
import http from "../../services/http";
import DataTable from "react-data-table-component";
import {
  Spinner, Container, Card, Input, Button, Row, Col, CardBody, CardTitle,
  Nav, NavItem, NavLink, TabContent, TabPane,
} from "reactstrap";
import classnames from "classnames";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";

const SalesReportByUser = () => {
  const api_url = getAPIURL();
  const vendorId = getVendorID();
  const [data, setData] = useState({ summary: {}, details: [] });
  const [activeTab, setActiveTab] = useState("summary");
  const [loading, setLoading] = useState(true);
  const [billerId, setBillerId] = useState("");
  const [users, setUsers] = useState([]);
  const [dateRange, setDateRange] = useState({
    start: new Date(new Date().setMonth(new Date().getMonth() - 1)).toISOString().split("T")[0],
    end: new Date().toISOString().split("T")[0],
  });

  useEffect(() => {
    fetchVendorUsers();
    fetchSalesReport();
  }, []);

  useEffect(() => {
    fetchSalesReport();
  }, [billerId, dateRange.start, dateRange.end]);

  const fetchVendorUsers = async () => {
    try {
      const u = await getVendorUsers(vendorId);
      setUsers(u);
    } catch (error) {
      console.error("Error fetching vendor users:", error);
    }
  };

  const fetchSalesReport = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ vendor_id: vendorId });
      if (billerId) params.append("biller_id", billerId);
      if (dateRange.start) params.append("start_date", dateRange.start);
      if (dateRange.end) params.append("end_date", dateRange.end);
      const { data } = await http.get(`${api_url}/reports/sales/vendor?${params}`);
      setData(data);
    } catch (error) {
      console.error("Error fetching sales report:", error);
    }
    setLoading(false);
  };

  const s = data.summary || {};
  const details = data.details || [];

  // Chart data: per-user comparison
  const chartData = details.map((u) => ({
    name: u.biller_name || "Unknown",
    Sales: u.total_sales || 0,
    Revenue: parseFloat(u.total_amount || 0),
    Tax: parseFloat(u.total_tax || 0),
  }));

  const txColumns = [
    { name: "User", selector: (row) => row.biller_name, sortable: true },
    { name: "Order ID", selector: (row) => row.order_id, sortable: true },
    { name: "Date", selector: (row) => row.order_date, sortable: true },
    { name: "Total", selector: (row) => getFormattedPrice(row.order_total), sortable: true },
    { name: "Tendered", selector: (row) => getFormattedPrice(row.amount_paid), sortable: true },
    { name: "Change", selector: (row) => getFormattedPrice(row.change_given), sortable: true },
    { name: "Tax", selector: (row) => getFormattedPrice(row.tax_collected), sortable: true },
  ];

  const allTransactions = details.flatMap((u) =>
    (u.transactions || []).map((t) => ({ ...t, biller_name: u.biller_name }))
  );

  return (
    <div className="page-body">
      <Container className="mt-2">
        <h4 className="text-center mb-3">Sales Report by User</h4>

        {/* Filters */}
        <div className="d-flex mb-3 gap-2">
          <Input type="select" value={billerId} onChange={(e) => setBillerId(e.target.value)}>
            <option value="">All Users</option>
            {users.map((u) => (
              <option key={u.id} value={u.id}>{u.name}</option>
            ))}
          </Input>
          <Input type="date" value={dateRange.start} onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })} />
          <Input type="date" value={dateRange.end} onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })} />
          <Button color="secondary" onClick={() => { setBillerId(""); setDateRange({ start: new Date(new Date().setMonth(new Date().getMonth() - 1)).toISOString().split("T")[0], end: new Date().toISOString().split("T")[0] }); }}>
            Reset
          </Button>
        </div>

        <Nav tabs className="mb-3">
          {["summary", "chart", "listing"].map((tab) => (
            <NavItem key={tab}>
              <NavLink className={classnames({ active: activeTab === tab })} onClick={() => setActiveTab(tab)} style={{ cursor: "pointer" }}>
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </NavLink>
            </NavItem>
          ))}
        </Nav>

        {loading ? (
          <div className="text-center"><Spinner color="primary" size="lg" /></div>
        ) : (
          <TabContent activeTab={activeTab}>
            <TabPane tabId="summary">
              <Row className="mb-4">
                {[
                  { label: "Users", value: details.length },
                  { label: "Total Sales", value: s.total_sales || 0 },
                  { label: "Revenue", value: getFormattedPrice(s.total_amount || 0) },
                  { label: "Tax", value: getFormattedPrice(s.total_tax || 0) },
                ].map((c, i) => (
                  <Col md="3" key={i}>
                    <Card className="p-3 shadow-sm text-center">
                      <CardBody>
                        <CardTitle tag="h6">{c.label}</CardTitle>
                        <h4>{c.value}</h4>
                      </CardBody>
                    </Card>
                  </Col>
                ))}
              </Row>

              {/* Per-user breakdown */}
              <Card className="p-3 shadow-sm">
                <h5 className="mb-3">Per-User Breakdown</h5>
                <DataTable
                  columns={[
                    { name: "User", selector: (row) => row.biller_name, sortable: true },
                    { name: "Sales", selector: (row) => row.total_sales || 0, sortable: true },
                    { name: "Revenue", selector: (row) => getFormattedPrice(row.total_amount || 0), sortable: true },
                    { name: "Tax", selector: (row) => getFormattedPrice(row.total_tax || 0), sortable: true },
                  ]}
                  data={details}
                  striped
                  highlightOnHover
                  pagination={false}
                />
              </Card>
            </TabPane>

            <TabPane tabId="chart">
              <Card className="p-3 shadow-sm">
                <h5 className="mb-3">User Sales Comparison</h5>
                {chartData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={400}>
                    <BarChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <Tooltip formatter={(v) => `$${v.toFixed(2)}`} />
                      <Legend />
                      <Bar dataKey="Revenue" fill="#28a745" />
                      <Bar dataKey="Tax" fill="#ffc107" />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <p className="text-center text-muted">No data to display</p>
                )}
              </Card>
            </TabPane>

            <TabPane tabId="listing">
              <Card className="p-3 shadow-sm">
                <h5 className="mb-3">All Transactions ({allTransactions.length})</h5>
                <DataTable columns={txColumns} data={allTransactions} pagination striped highlightOnHover />
              </Card>
            </TabPane>
          </TabContent>
        )}
      </Container>
    </div>
  );
};

export default SalesReportByUser;
